import React, { useRef, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { FiArrowLeft, FiMic, FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDuration, intervalToDuration } from 'date-fns';
import pt from 'date-fns/locale/pt';

import { usePermit } from 'utils/hooks/usePermit';
import { useModal } from 'hooks/modal';
import { show } from 'store/modules/dialog/actions';
import getValidationErrors from 'services/getValidationErros';
import api from 'services/api';
import AppError from 'utils/AppError';
import { Permissao, StatusRoteiroPromo, Tipo } from 'utils/variables';

import RichTextArea from 'components/RichTextArea';
import Button from 'components/Button';
import Input from 'components/Input';
import Card from 'components/Card';
import TextArea from 'components/TextArea';
import Select from 'components/Select';

import {
  Container,
  Content,
  Footer,
  Form,
  FormGroup,
  RoteiroLocucao,
} from './styles';
import { RoteiroPromo, Usuario } from 'pages/@types';
import Loader from 'components/ContentLoader/GenericLoader';

// configuração do validator
const schema = Yup.object().shape({
  retranca: Yup.string()
    .required('Campo Obrigatório')
    .matches(/^[a-zA-Z0-9- ./]*$/, 'Retranca contém caracteres especiais')
    .max(26, 'Retranca excedeu o limite de 26 caracteres'),
  roteiro: Yup.string()
    .required('Campo Obrigatório')
    .max(65500, 'Tamanho do roteiro excedido'),
  tipo: Yup.string().required('Campo Obrigatório'),
  duracao: Yup.number()
    .required('Campo Obrigatório')
    .typeError('A duração deve ser um número')
    .min(1, 'Defina a duração'),
});

const schemaLocucao = schema.shape({
  comunicador: Yup.number()
    .required('Campo Obrigatório')
    .typeError('Campo Obrigatório'),
  locucao: Yup.string().required('Campo Obrigatório'),
});

export default function RoteiroPromoStore() {
  // react-router-dom
  const navigate = useNavigate();

  /**
   * USE STATE
   */
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingComunicadores, setLoadingComunicadores] = useState(true);
  const [comunicadores, setComunicadores] = useState([]);
  const [initialData, setInitialData] = useState({} as RoteiroPromo);
  const [locucao, setLocucao] = useState('');
  const { showModal, hideModal } = useModal();
  const [disabledForm, setDisabledForm] = useState(true);
  /**
   * USE PARAMS
   */
  const { roteiroId } = useParams<{ roteiroId: string }>();

  /**
   * REDUX
   */
  const hasAvaliador = usePermit([], [Permissao.AVALIADOR]);
  const hasLocucao = usePermit([], [Permissao.LOCUCAO]);
  const dispatch = useDispatch();

  /**
   * USE REF
   */
  const formRef = useRef<FormHandles>(null);
  const avaliacaoFormRef = useRef<FormHandles>(null);
  const statusRef = useRef('');
  /**
   * USE CALLBACK
   */

  const handleSuccessMessage = useCallback(
    ({ contentHTML, redirectTo }) => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  useEffect(() => {
    if (
      (roteiroId &&
        initialData.status !== StatusRoteiroPromo.EMPROCESSO &&
        initialData.status !== StatusRoteiroPromo.APROVACAO) ||
      loadingSubmit
    )
      setDisabledForm(true);
    else setDisabledForm(false);
  }, [initialData.status, loadingSubmit, roteiroId]);

  const handleSubmit = useCallback(
    async data => {
      setLoadingSubmit(true);
      if (formRef.current) formRef.current.setErrors({});
      try {
        if (statusRef.current === StatusRoteiroPromo.LOCUCAO)
          await schemaLocucao.validate(data, { abortEarly: false });
        else await schema.validate(data, { abortEarly: false });

        if (!data.comunicador) delete data.comunicador;

        let newRoteiroId = 0;

        if (!roteiroId) {
          const response = await api.post('/roteiropromo', data);
          if (response?.data) newRoteiroId = response.data.id;
        } else await api.put(`/roteiropromo/${roteiroId}`, data);

        if (
          statusRef.current === StatusRoteiroPromo.APROVACAO ||
          statusRef.current === StatusRoteiroPromo.LOCUCAO
        )
          await api.patch(`/roteiropromo/${newRoteiroId || roteiroId}`, {
            observacao: data.observacao,
            status: statusRef.current,
          });

        handleSuccessMessage({
          contentHTML: `<h3>Roteiro ${
            roteiroId ? 'atualizado' : 'criado'
          }!</h3> <p>Você será redirecionado em 3 segundos</p>`,
          redirectTo: '/roteiropromo',
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) toast.error(err.message);
        else {
          const appError = err as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      } finally {
        setLoadingSubmit(false);
      }
    },
    [roteiroId, handleSuccessMessage],
  );

  const handleUpdateStatus = useCallback(
    async status => {
      setLoadingSubmit(true);
      try {
        await api.patch(`/roteiropromo/${roteiroId}`, {
          status,
        });
        handleSuccessMessage({
          contentHTML: `<h3>Roteiro atualizado!</h3> <p>Você será redirecionado em 3 segundos</p>`,
          redirectTo: '/roteiropromo',
          type: 'success',
        });
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      } finally {
        setLoadingSubmit(false);
      }
    },
    [handleSuccessMessage, roteiroId],
  );

  const handleSubmitAvaliacao = useCallback(
    async (data, status) => {
      try {
        setLoadingSubmit(true);
        if (formRef.current) await handleSubmit(formRef.current.getData());
        await api.patch(`/roteiropromo/${roteiroId}`, {
          observacao: data.observacao,
          status:
            initialData.comunicador && status === StatusRoteiroPromo.FINALIZADO
              ? StatusRoteiroPromo.LOCUCAO
              : status,
        });
        hideModal();
        // handleSuccessMessage({
        //   contentHTML: `<h3>Roteiro Promo atualizado!</h3> <p>Você será redirecionado em 3 segundos</p>`,
        //   redirectTo: '/roteiropromo',
        //   type: 'success',
        // });
        setLoadingSubmit(false);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) toast.error(err.message);
        else {
          const appError = err as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      } finally {
        setLoadingSubmit(false);
      }
    },
    [hideModal, initialData.comunicador, roteiroId],
  );

  const renderAvaliacao = useCallback(() => {
    return (
      <Form
        ref={avaliacaoFormRef}
        onSubmit={data => handleSubmitAvaliacao(data, statusRef.current)}
      >
        <TextArea
          placeholder="Observação"
          name="observacao"
          height={150}
          disable={false}
          errorField=""
          icon=""
        />
        <div className="actions">
          <Button color="default" onClick={() => hideModal()}>
            CANCELAR
          </Button>
          <Button type="submit" color="primary">
            CONFIRMAR
          </Button>
        </div>
      </Form>
    );
  }, [handleSubmitAvaliacao, hideModal]);

  const handleAvaliacao = useCallback(() => {
    showModal({
      title: 'OBSERVAÇÕES',
      component: renderAvaliacao(),
      width: '30vw',
    });
  }, [renderAvaliacao, showModal]);

  const renderStatusButtons = useCallback(() => {
    return (
      <div>
        {!roteiroId || initialData.status === StatusRoteiroPromo.EMPROCESSO ? (
          <div>
            <Button
              type="submit"
              color="primary"
              disabled={loadingSubmit}
              onClick={() => {
                statusRef.current = StatusRoteiroPromo.APROVACAO;
              }}
            >
              SALVAR E ENVIAR PARA APROVAÇÃO
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={loadingSubmit}
              onClick={() => {
                statusRef.current = StatusRoteiroPromo.LOCUCAO;
              }}
            >
              SALVAR E ENVIAR PARA LOCUÇÃO
            </Button>
            <Button type="submit" color="primary" disabled={loadingSubmit}>
              <FiSave />
              SALVAR
            </Button>
          </div>
        ) : initialData.status === StatusRoteiroPromo.APROVACAO &&
          hasAvaliador ? (
          <div>
            <Button
              type="button"
              color="danger"
              disabled={loadingSubmit || !hasAvaliador}
              onClick={() => {
                statusRef.current = StatusRoteiroPromo.EMPROCESSO;
                handleAvaliacao();
              }}
            >
              REPROVAR
            </Button>
            <Button
              type="button"
              color="success"
              disabled={loadingSubmit || !hasAvaliador}
              onClick={() => {
                statusRef.current = StatusRoteiroPromo.FINALIZADO;
                handleAvaliacao();
              }}
            >
              {initialData.comunicador
                ? 'APROVAR E ENVIAR PARA LOCUÇÃO'
                : 'APROVAR'}
            </Button>
          </div>
        ) : initialData.status === StatusRoteiroPromo.LOCUCAO &&
          (hasLocucao || hasAvaliador) ? (
          <Button
            type="submit"
            color="success"
            disabled={!initialData.comunicador}
            onClick={() => {
              handleUpdateStatus(StatusRoteiroPromo.FINALIZADO);
            }}
          >
            <FiMic />
            CONFIRMAR GRAVAÇÃO
          </Button>
        ) : (
          initialData.status !== StatusRoteiroPromo.FINALIZADO && (
            <Button
              type="button"
              color="danger"
              disabled={loadingSubmit}
              onClick={() => {
                handleUpdateStatus(StatusRoteiroPromo.EMPROCESSO);
              }}
            >
              VOLTAR PARA ALTERAÇÃO
            </Button>
          )
        )}
      </div>
    );
  }, [
    handleAvaliacao,
    handleUpdateStatus,
    hasAvaliador,
    hasLocucao,
    initialData.comunicador,
    initialData.status,
    loadingSubmit,
    roteiroId,
  ]);

  /**
   * USE EFFECT
   */
  useEffect(() => {
    async function loadRoteiro() {
      setLoading(true);
      try {
        const response = await api.get(`/roteiropromo/${roteiroId}`);

        if (response?.data) {
          setInitialData({
            ...response.data,
            ...(response.data.comunicador && {
              comunicador: {
                value: response.data.comunicador?.id,
                label: response.data.comunicador?.nome,
              },
            }),
            tipo: {
              value: response.data.tipo,
              label: response.data.tipo.toUpperCase(),
            },
          });
          setLocucao(response.data.locucao);
        }
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      } finally {
        setLoading(false);
      }
    }
    if (roteiroId) loadRoteiro();
    else setLoading(false);
  }, [roteiroId]);

  useEffect(() => {
    async function loadComunicadores() {
      setLoadingComunicadores(true);
      try {
        const response = await api.get('/usuarios', {
          params: { locucao: true },
        });
        if (response?.data)
          setComunicadores(
            response.data.docs
              .filter((u: Usuario) => u.status !== 'inativo')
              .map((u: Usuario) => ({
                value: u.id,
                label: u.nome,
                ...u,
              })),
          );
        else toast.error('Falha de comunicação com o sistema');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      } finally {
        setLoadingComunicadores(false);
      }
    }
    loadComunicadores();
  }, []);

  return (
    <Container>
      <Card height="auto">
        {loading ? (
          <Loader type="default" />
        ) : (
          <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
            <Content>
              <header>
                <h4>ROTEIRO DE PRODUÇÃO</h4>
              </header>
              <FormGroup>
                <div>
                  <section>
                    <h5>Retranca: </h5>
                  </section>
                  <Input
                    name="retranca"
                    placeholder="Retranca *"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section>
                    <h5>Duração: </h5>
                  </section>
                  <Input
                    name="duracao"
                    type="number"
                    placeholder="Duração *"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section>
                    <h5>Tipo: </h5>
                  </section>
                  <Select
                    name="tipo"
                    options={Object.keys(Tipo).map(t => ({
                      value: t.toLowerCase(),
                      label: t,
                    }))}
                    placeholder="Tipo *"
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section>
                    <h5>Comunicador: </h5>
                  </section>
                  <Select
                    errorField=""
                    type="avatar"
                    name="comunicador"
                    placeholder="Selecione o comunicador ..."
                    options={comunicadores}
                    isSearchable
                    isClearable
                    disabled={disabledForm || loadingComunicadores}
                    fill={false}
                  />
                </div>
              </FormGroup>
              <RoteiroLocucao>
                <div>
                  <section>
                    <h5>Roteiro: </h5>
                  </section>
                  <RichTextArea
                    height="calc(100vh - 355px)"
                    name="roteiro"
                    errorField=""
                    disabled={disabledForm}
                  />
                </div>
                <div>
                  <section>
                    <h5>Texto a ser gravado: </h5>
                  </section>
                  <TextArea
                    icon=""
                    name="locucao"
                    errorField=""
                    disabled={disabledForm}
                    height="calc(100vh - 445px)"
                    onChange={e => {
                      setLocucao(e.target.value);
                    }}
                  />
                  <h2>
                    {formatDuration(
                      intervalToDuration({
                        start: 0,
                        end: (locucao.trim().split(/\s+/).length / 2.5) * 1000,
                      }),
                      {
                        locale: pt,
                      },
                    )}
                  </h2>
                  <h3>
                    {locucao
                      ? `${locucao.trim().split(/\s+/).length} palavra(s)`
                      : ''}
                  </h3>
                </div>
              </RoteiroLocucao>
            </Content>
            <Footer>
              <Button
                color="primary"
                type="button"
                onClick={() => {
                  navigate(-1);
                }}
              >
                <FiArrowLeft />
                VOLTAR
              </Button>

              {!loading && renderStatusButtons()}
            </Footer>
          </Form>
        )}
      </Card>
    </Container>
  );
}
